/**
 * Parser for the advanced search syntax.
 *
 * Specification: docs/concepts/concept_advanced_search_syntax.md (section 4).
 * Test cases shared with the backend parser (backend/app/core/query_syntax.py):
 * shared/query_syntax_cases.json. Both parsers must implement the same
 * algorithm; only this one reports hints (warnings) and offers fixes.
 *
 * A query is one or more OR groups; each group is a list of chips joined by
 * AND / NOT / spaces. AND binds tighter than OR, there are no brackets.
 */

export const DEFAULT_MAX_CHIPS = 10;
export const DEFAULT_MAX_PHRASE_WORDS = 10;

export type ChipKind = 'words' | 'phrase';
export type SyntaxOperator = 'AND' | 'OR' | 'NOT';

export interface Chip {
  kind: ChipKind;
  text: string;
  negated: boolean;
  /** Condition (hard filter); false for search words (today's soft matching). */
  hard: boolean;
  /** Joined to the previous chip by an explicit AND; kept for the canonical form. */
  andBefore: boolean;
  /** Phrase whose closing quote was added by the parser. */
  open: boolean;
}

export type SyntaxWarning =
  | { code: 'quote_closed' }
  | { code: 'stray_quote' }
  | { code: 'brackets' }
  | { code: 'lowercase_operator'; word: string; positions: number[] }
  | { code: 'dangling_operator'; operator: SyntaxOperator; trailing: boolean }
  | { code: 'exclusion_only' }
  | { code: 'not_only_group' }
  | { code: 'too_many_chips' }
  | { code: 'phrase_too_long' };

export interface ParsedQuery {
  groups: Chip[][];
  /** Text of the search-word chips. */
  softText: string;
  /** All positive text (search words + positive conditions). */
  searchText: string;
  exclusionOnly: boolean;
  canonical: string;
  warnings: SyntaxWarning[];
  /** Operator typed last that still waits for a word. */
  trailingOperator: SyntaxOperator | null;
}

export interface ParseOptions {
  maxChips?: number;
  maxPhraseWords?: number;
}

type TokenKind = 'WORD' | 'PHRASE' | SyntaxOperator;

interface Token {
  kind: TokenKind;
  text: string;
  open: boolean;
  /** Start index of a word in the input (for fixes). */
  position: number;
  /** Word read after a `-`. */
  afterDash: boolean;
}

interface ChipBuilder {
  kind: ChipKind;
  words: string[];
  negated: boolean;
  andBefore: boolean;
  open: boolean;
  /** Words cut off a too long phrase; they stay search text. */
  extra: string[];
}

const TYPOGRAPHIC_QUOTES = /[\u201e\u201c\u201d\u00ab\u00bb]/g;
const OPERATOR_WORDS: Record<string, SyntaxOperator> = { AND: 'AND', OR: 'OR', NOT: 'NOT', '|': 'OR' };
// An unclosed phrase ends right before the next standalone operator.
const OPERATOR_AHEAD = /(?:^|\s)(?:AND|OR|NOT|\|)(?=\s|$)/;
const ADVANCED_PATTERNS = [
  /["\u201e\u201c\u201d\u00ab\u00bb]/,
  /(?:^|\s)(?:AND|OR|NOT|\|)(?=\s|$)/,
  /(?:^|\s)-+[^\s()-]/,
  /[()]/,
  /\S\s+(?:and|or|not)\s+\S/i,
];
const LOWERCASE_OPERATOR = /^(?:and|or|not)$/i;

const isSpace = (char: string) => /\s/.test(char);
const isBracket = (char: string) => char === '(' || char === ')';

/** Whether the input uses (or tries to use) the syntax; plain queries keep today's behaviour. */
export function isAdvancedQuery(raw: string): boolean {
  return ADVANCED_PATTERNS.some((pattern) => pattern.test(raw));
}

function readWord(s: string, i: number): [string, number] {
  let j = i;
  while (j < s.length && !isSpace(s[j]) && s[j] !== '"' && !isBracket(s[j])) j++;
  return [s.slice(i, j), j];
}

/** Read the phrase opened by the quote at `i`. */
function readPhrase(s: string, i: number): { text: string; open: boolean; next: number } {
  const end = s.indexOf('"', i + 1);
  let text: string;
  let next: number;
  if (end >= 0) {
    text = s.slice(i + 1, end);
    next = end + 1;
  } else {
    const rest = s.slice(i + 1);
    const match = OPERATOR_AHEAD.exec(rest);
    text = match ? rest.slice(0, match.index) : rest;
    next = match ? i + 1 + match.index : s.length;
  }
  return { text: text.split(/\s+/).filter(Boolean).join(' '), open: end < 0, next };
}

/** A quote glued to the end of a word (`list" foo`) closes nothing and is ignored. */
function isStrayQuote(s: string, i: number): boolean {
  const before = i > 0 ? s[i - 1] : ' ';
  const after = i + 1 < s.length ? s[i + 1] : ' ';
  return !isSpace(before) && !isBracket(before) && (isSpace(after) || isBracket(after));
}

function token(kind: TokenKind, text = '', extra: Partial<Token> = {}): Token {
  return { kind, text, open: false, position: -1, afterDash: false, ...extra };
}

function tokenize(raw: string, warnings: SyntaxWarning[]): Token[] {
  // Every replacement is one character for one, so positions match the input.
  const s = raw.replace(TYPOGRAPHIC_QUOTES, '"');
  const tokens: Token[] = [];
  const pushPhrase = (at: number, negated: boolean): number => {
    const phrase = readPhrase(s, at);
    if (phrase.text) {
      if (negated) tokens.push(token('NOT'));
      tokens.push(token('PHRASE', phrase.text, { open: phrase.open }));
      if (phrase.open) warnings.push({ code: 'quote_closed' });
    }
    return phrase.next;
  };
  let i = 0;
  while (i < s.length) {
    const char = s[i];
    if (isSpace(char)) {
      i++;
      continue;
    }
    if (isBracket(char)) {
      warnings.push({ code: 'brackets' });
      i++;
      continue;
    }
    if (char === '-') {
      let j = i;
      while (j < s.length && s[j] === '-') j++;
      if (j >= s.length || isSpace(s[j]) || isBracket(s[j])) {
        i = j;
        continue;
      }
      if (s[j] === '"') {
        i = pushPhrase(j, true);
        continue;
      }
      const [word, next] = readWord(s, j);
      const operator = OPERATOR_WORDS[word];
      if (operator) tokens.push(token(operator));
      else tokens.push(token('NOT'), token('WORD', word, { position: j, afterDash: true }));
      i = next;
      continue;
    }
    if (char === '"') {
      if (isStrayQuote(s, i)) {
        warnings.push({ code: 'stray_quote' });
        i++;
        continue;
      }
      i = pushPhrase(i, false);
      continue;
    }
    const [word, next] = readWord(s, i);
    const operator = OPERATOR_WORDS[word];
    tokens.push(operator ? token(operator) : token('WORD', word, { position: i }));
    i = next;
  }
  tokens.forEach((t, index) => {
    const between = index > 0 && index < tokens.length - 1;
    if (t.kind === 'WORD' && !t.afterDash && between && LOWERCASE_OPERATOR.test(t.text)) {
      const existing = warnings.find((w) => w.code === 'lowercase_operator' && w.word === t.text);
      if (existing && existing.code === 'lowercase_operator') existing.positions.push(t.position);
      else warnings.push({ code: 'lowercase_operator', word: t.text, positions: [t.position] });
    }
  });
  return tokens;
}

function chipText(chip: Pick<Chip, 'kind' | 'text'>): string {
  return chip.kind === 'phrase' ? `"${chip.text}"` : chip.text;
}

/** Canonical form: AND / OR / NOT in capitals, closed quotes, no brackets. */
export function serializeQuery(groups: Chip[][]): string {
  return groups
    .map((group) =>
      group
        .map((chip, index) => (chip.negated ? 'NOT ' : index > 0 && chip.andBefore ? 'AND ' : '') + chipText(chip))
        .join(' '),
    )
    .join(' OR ');
}

function warningKey(w: SyntaxWarning): string {
  if (w.code === 'dangling_operator') return `${w.code}:${w.operator}:${w.trailing}`;
  if (w.code === 'lowercase_operator') return `${w.code}:${w.word}`;
  return w.code;
}

export function parseQuery(raw: string, options: ParseOptions = {}): ParsedQuery {
  const maxChips = options.maxChips ?? DEFAULT_MAX_CHIPS;
  const maxPhraseWords = options.maxPhraseWords ?? DEFAULT_MAX_PHRASE_WORDS;
  const warnings: SyntaxWarning[] = [];
  const tokens = tokenize(raw ?? '', warnings);
  const groups: ChipBuilder[][] = [[]];
  let pendingAnd = false;
  let joinable = false;
  let trailingOperator: SyntaxOperator | null = null;

  const phraseChip = (t: Token, negated: boolean, andBefore: boolean): ChipBuilder => {
    const words = t.text.split(' ');
    if (words.length > maxPhraseWords) warnings.push({ code: 'phrase_too_long' });
    return {
      kind: 'phrase',
      words: words.slice(0, maxPhraseWords),
      negated,
      andBefore,
      open: t.open,
      extra: words.slice(maxPhraseWords),
    };
  };
  const dangling = (operator: SyntaxOperator, trailing: boolean) => {
    warnings.push({ code: 'dangling_operator', operator, trailing });
    if (trailing) trailingOperator = operator;
  };

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const following = tokens[i + 1];
    const current = groups[groups.length - 1];
    if (t.kind === 'AND' || t.kind === 'OR') {
      const valid = current.length > 0 && !!following && following.kind !== 'AND' && following.kind !== 'OR';
      if (!valid) dangling(t.kind, !following && current.length > 0);
      if (valid && t.kind === 'OR') groups.push([]);
      // AND directly before NOT is absorbed: `a AND NOT b` = `a NOT b`.
      pendingAnd = valid && t.kind === 'AND' && following.kind !== 'NOT';
      joinable = false;
    } else if (t.kind === 'NOT') {
      if (following && (following.kind === 'WORD' || following.kind === 'PHRASE')) {
        current.push(
          following.kind === 'PHRASE'
            ? phraseChip(following, true, false)
            : { kind: 'words', words: [following.text], negated: true, andBefore: false, open: false, extra: [] },
        );
        i++;
      } else {
        dangling('NOT', !following);
      }
      pendingAnd = false;
      joinable = false;
    } else if (t.kind === 'WORD') {
      if (joinable) current[current.length - 1].words.push(t.text);
      else current.push({ kind: 'words', words: [t.text], negated: false, andBefore: pendingAnd, open: false, extra: [] });
      pendingAnd = false;
      joinable = true;
    } else {
      current.push(phraseChip(t, false, pendingAnd));
      pendingAnd = false;
      joinable = false;
    }
  }

  // Search text keeps the words of chips dropped by the limits, in reading order.
  const searchWords: string[] = [];
  let count = 0;
  let limited: ChipBuilder[][] = [];
  for (const group of groups) {
    const kept: ChipBuilder[] = [];
    for (const chip of group) {
      count++;
      if (count <= maxChips) kept.push(chip);
      if (!chip.negated) searchWords.push(chip.words.join(' '), ...chip.extra);
    }
    if (kept.length) limited.push(kept);
  }
  if (count > maxChips) warnings.push({ code: 'too_many_chips' });

  if (limited.length > 1) {
    const positiveGroups = limited.filter((group) => group.some((chip) => !chip.negated));
    if (positiveGroups.length && positiveGroups.length < limited.length) {
      warnings.push({ code: 'not_only_group' });
      limited = positiveGroups;
    }
  }
  const exclusionOnly = limited.length > 0 && limited.every((group) => group.every((chip) => chip.negated));
  if (exclusionOnly) warnings.push({ code: 'exclusion_only' });

  const multi = limited.length > 1;
  const result: Chip[][] = limited.map((group) => {
    group[0].andBefore = false;
    return group.map((chip, index) => {
      const nextAnd = index + 1 < group.length && group[index + 1].andBefore;
      return {
        kind: chip.kind,
        text: chip.words.join(' '),
        negated: chip.negated,
        hard: chip.negated || chip.kind === 'phrase' || multi || chip.andBefore || nextAnd,
        andBefore: chip.andBefore,
        open: chip.open,
      };
    });
  });

  const seen = new Set<string>();
  return {
    groups: result,
    softText: result
      .flat()
      .filter((chip) => chip.kind === 'words' && !chip.hard)
      .map((chip) => chip.text)
      .join(' '),
    searchText: searchWords.join(' '),
    exclusionOnly,
    canonical: serializeQuery(result),
    warnings: warnings.filter((w) => {
      const key = warningKey(w);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }),
    trailingOperator,
  };
}

/** The query after applying the one-click fix for `warning`, or null if the warning has no fix. */
export function fixQuery(raw: string, warning: SyntaxWarning, options: ParseOptions = {}): string | null {
  switch (warning.code) {
    case 'lowercase_operator': {
      const upper = warning.word.toUpperCase();
      return warning.positions.reduce(
        (query, position) => query.slice(0, position) + upper + query.slice(position + warning.word.length),
        raw,
      );
    }
    case 'dangling_operator':
      return warning.trailing ? null : parseQuery(raw, options).canonical;
    case 'brackets':
    case 'stray_quote':
    case 'not_only_group':
      return parseQuery(raw, options).canonical;
    default:
      return null;
  }
}

/** Canonical query without one chip (results page); '' when it was the last one. */
export function removeChip(raw: string, groupIndex: number, chipIndex: number, options: ParseOptions = {}): string {
  const groups = parseQuery(raw, options)
    .groups.map((group, g) => (g === groupIndex ? group.filter((_, c) => c !== chipIndex) : group))
    .filter((group) => group.length > 0);
  return serializeQuery(groups);
}

export const PHRASE_HINT_MIN_RESULTS = 5;

/**
 * The exact phrase to offer for a plain query of 2-4 words (concept §5.5),
 * or null when the hint does not apply.
 */
export function phraseHintQuery(raw: string): string | null {
  const words = raw.trim().split(/\s+/).filter(Boolean);
  if (isAdvancedQuery(raw) || words.length < 2 || words.length > 4) return null;
  return `"${words.join(' ')}"`;
}

/**
 * Whether the backend read the query differently than this parser (concept
 * §7.2). `normalized` is `query_interpretation.normalized_query`, absent for
 * plain queries.
 */
export function syntaxDrift(raw: string, normalized: string | null | undefined): boolean {
  const local = isAdvancedQuery(raw) ? parseQuery(raw).canonical : null;
  return local !== (normalized ?? null);
}
